/**
 * A wireframe of the page a template will build, drawn from the renderer's
 * own shape data.
 *
 * Why this exists: the picker used to be a dropdown of five names. "Bold
 * Banner" and "Rapid Response" tell an operator nothing about what they are
 * about to get, and the difference between these templates is not styling —
 * it is which sections exist and in what order. Choosing blind and then
 * waiting out a generation to find out is the slow way to learn a shape.
 *
 * The shape arrives from GET /landing-pages/templates, merged server-side
 * from the same TREATMENTS registry the renderer uses. Nothing here is a
 * local copy, so this cannot drift from the page that actually gets built.
 *
 * COLOURS INSIDE THE FRAME ARE DELIBERATELY NOT THEME TOKENS. This wireframe
 * depicts a rendered landing page, not app chrome, and it sits behind a
 * toggle with an iframe of that same page — which is already `bg-white`. Left
 * on themed tokens the hero inverted to a light band in dark mode, showing an
 * operator the opposite of the page they were about to build. Page colours
 * here, theme tokens for the caption below the frame, which IS app chrome.
 */
import { Image, Phone } from 'lucide-react';

import type { LandingTemplateShape } from '@/services/landingPagesService';

/** What each section is FOR, in the words of the person choosing it. */
const SECTION_COPY: Record<string, { label: string; note: string }> = {
  form: { label: 'Form', note: 'the lead capture' },
  benefits: { label: 'Benefits', note: 'why them' },
  steps: { label: 'How it works', note: 'three steps' },
  proof: { label: 'Proof', note: 'credentials, reviews' },
  offer: { label: 'Offer', note: 'the specific promise' },
  faq: { label: 'FAQ', note: 'objections' },
  final: { label: 'Closing CTA', note: 'last ask' },
};

const Bar = ({ w, tone = 'muted' }: { w: string; tone?: 'muted' | 'ink' }) => (
  <div
    className={`h-1.5 rounded-full ${tone === 'ink' ? 'bg-black/25' : 'bg-black/10'}`}
    style={{ width: w }}
  />
);

/** Each section gets a distinct silhouette — a stack of identical grey
 *  rectangles would communicate the ORDER but not the shape, and the shape is
 *  the thing being chosen. */
function SectionBody({ name }: { name: string }) {
  switch (name) {
    case 'form':
      return (
        <div className="space-y-1.5">
          <div className="h-4 rounded border border-black/15 bg-white" />
          <div className="h-4 rounded border border-black/15 bg-white" />
          <div className="h-4 w-2/3 rounded bg-coral/70" />
        </div>
      );
    case 'benefits':
      return (
        <div className="grid grid-cols-3 gap-1.5">
          <div className="col-span-2 h-9 rounded bg-black/[0.06]" />
          <div className="h-9 rounded bg-black/[0.06]" />
          <div className="h-9 rounded bg-black/[0.06]" />
          <div className="col-span-2 h-9 rounded bg-black/[0.06]" />
        </div>
      );
    case 'steps':
      return (
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="space-y-1">
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-coral/20 text-[8px] font-bold text-coral">
                {n}
              </div>
              <Bar w="100%" />
              <Bar w="70%" />
            </div>
          ))}
        </div>
      );
    case 'proof':
      return (
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="h-6 flex-1 rounded bg-black/[0.06]" />
          ))}
        </div>
      );
    case 'offer':
      return (
        <div className="space-y-1.5 rounded bg-coral/[0.06] p-2">
          <Bar w="55%" tone="ink" />
          <Bar w="85%" />
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-1">
          {['78%', '62%', '70%'].map((w, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded border border-black/10 px-1.5 py-1"
            >
              <Bar w={w} />
              <span className="text-[9px] leading-none text-black/40">+</span>
            </div>
          ))}
        </div>
      );
    case 'final':
      return (
        <div className="flex flex-col items-center gap-1.5 py-1">
          <Bar w="60%" tone="ink" />
          <div className="h-4 w-24 rounded bg-coral/70" />
        </div>
      );
    default:
      return <Bar w="100%" />;
  }
}

export function TemplateSkeleton({
  shape,
  templateName,
}: {
  shape: LandingTemplateShape;
  templateName: string;
}) {
  const split = shape.hero_form_layout === 'split';

  return (
    <div className="mx-auto w-full max-w-md">
      {/* The frame is deliberately page-shaped rather than a list — the point
          is recognising a layout at a glance. */}
      <div className="overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
        {shape.sticky_call_bar && (
          <div className="flex items-center justify-center gap-1 bg-black/85 py-1 text-[9px] font-medium text-white">
            <Phone className="h-2.5 w-2.5" />
            Sticky call bar
          </div>
        )}

        {/* Hero. Dark by convention across every treatment, so the wireframe
            is dark too — an operator should recognise the band. */}
        <div className="relative bg-black/90 p-4">
          <div className={split ? 'grid grid-cols-[1.4fr_1fr] gap-3' : ''}>
            <div className="space-y-1.5">
              <div className="h-2.5 w-4/5 rounded-full bg-white/85" />
              <div className="h-2.5 w-3/5 rounded-full bg-white/60" />
              <div className="h-1.5 w-full rounded-full bg-white/25" />
              <div className="mt-2 h-5 w-24 rounded bg-coral" />
            </div>
            {split && (
              <div className="space-y-1 rounded border-t-2 border-coral bg-white p-2">
                <div className="h-1.5 w-2/3 rounded-full bg-black/30" />
                <div className="h-3 rounded border border-black/15" />
                <div className="h-3 rounded border border-black/15" />
                <div className="h-3 rounded bg-coral/70" />
              </div>
            )}
          </div>
          {/* Bottom-LEFT, not top-right: on a split treatment the form card
              occupies the top-right corner and the badge landed on top of
              it, half clipped by the hero edge. */}
          {shape.hero_uses_image && (
            <div className="mt-2 inline-flex items-center gap-1 rounded bg-white/20 px-1.5 py-0.5 text-[9px] text-white">
              <Image className="h-2.5 w-2.5" />
              hero needs a photo
            </div>
          )}
        </div>

        <div className="divide-y divide-black/[0.07]">
          {shape.sections.map((name, i) => {
            const copy = SECTION_COPY[name] ?? { label: name, note: '' };
            return (
              <div key={`${name}-${i}`} className="px-4 py-2.5">
                <div className="mb-1.5 flex items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-black/70">
                    {copy.label}
                  </span>
                  <span className="text-[10px] text-black/45">{copy.note}</span>
                </div>
                <SectionBody name={name} />
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-3 text-center text-xs text-muted-foreground">
        {templateName} builds {shape.sections.length + 1} sections
        {split ? ', form beside the hero' : ', form in its own band'}
        {shape.hero_uses_image ? ', hero needs a photo' : ''}.
      </p>
    </div>
  );
}
